import { differenceInCalendarDays, format, formatDistanceToNow, startOfDay } from "date-fns";
import { MessageCircle, Pencil, PlusCircle, UserCheck } from "lucide-react";
import type { Activity, ActivityAction } from "@/types/activity";

export const ACTIVITY_ACTION_META: Record<
  ActivityAction,
  {
    label: string;
    icon: typeof MessageCircle;
    tone: "success" | "warning" | "primary" | "secondary";
    description: (actor: string, target: string) => string;
  }
> = {
  created: {
    label: "Created",
    icon: PlusCircle,
    tone: "success",
    description: (actor, target) => `${actor} created ${target}`,
  },
  edited: {
    label: "Edited",
    icon: Pencil,
    tone: "warning",
    description: (actor, target) => `${actor} updated ${target}`,
  },
  assigned: {
    label: "Assigned",
    icon: UserCheck,
    tone: "primary",
    description: (actor, target) => `${actor} assigned ${target}`,
  },
  commented: {
    label: "Commented",
    icon: MessageCircle,
    tone: "secondary",
    description: (actor, target) => `${actor} commented on ${target}`,
  },
};

export function formatActivityDescription(activity: Activity) {
  const actorName = activity.actor.name || "Someone";
  const meta = ACTIVITY_ACTION_META[activity.action];
  return meta?.description(actorName, activity.target.title) ?? `${actorName} acted on ${activity.target.title}`;
}

export function formatActivityTime(timestamp: string) {
  return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
}

export function getActivitySectionLabel(date: Date, referenceDate = new Date()) {
  const daysAgo = differenceInCalendarDays(startOfDay(referenceDate), startOfDay(date));

  if (daysAgo === 0) return "Today";
  if (daysAgo === 1) return "Yesterday";
  if (daysAgo <= 7) return "Last 7 Days";
  return format(date, "PPP");
}

export function groupActivitiesBySection(activities: Activity[], referenceDate = new Date()) {
  const sorted = [...activities].sort(
    (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
  );

  const bucket = new Map<string, Activity[]>();

  for (const activity of sorted) {
    const label = getActivitySectionLabel(new Date(activity.timestamp), referenceDate);
    const items = bucket.get(label) ?? [];
    items.push(activity);
    bucket.set(label, items);
  }

  return Array.from(bucket.entries()).map(([label, items]) => ({ label, items }));
}

export function filterActivitiesByActor(
  activities: Activity[],
  currentUserId?: string | null,
  filter: "all" | "mine" = "all"
) {
  if (filter === "mine" && currentUserId) {
    return activities.filter((activity) => activity.actor.id === currentUserId);
  }
  return activities;
}
